// filepath: src/context/ThemeContext.tsx
import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';

interface ThemeContextProps {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(localStorage.getItem("IsDarkModeActivated") === "true" || window.matchMedia("(prefers-color-scheme: dark)").matches);

  useEffect(() => {
    localStorage.setItem("IsDarkModeActivated", isDarkMode.toString());
    const themeLink = document.getElementById("theme-link") as HTMLLinkElement;
        if (themeLink) {
          themeLink.href = isDarkMode
            ? "/primereact/resources/themes/lara-dark-cyan/theme.css"
            : "/primereact/resources/themes/lara-light-cyan/theme.css";
        } else {
          console.error("Theme link element not found in the DOM!");
        }
    const link = document.querySelector("link[data-theme]") as HTMLLinkElement;
    if (link) {
      link.href = isDarkMode ? "/assets/css/darkmode.css" : "/assets/css/lightmode.css";
    }
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDarkMode ? "dark" : "light");
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");

    // Remove the dynamic import

  }, [isDarkMode]);

  // Sync theme across browser tabs via localStorage "storage" event
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "IsDarkModeActivated" && e.newValue !== null) {
        const newDark = e.newValue === "true";
        setIsDarkMode(newDark);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  const contextValue = useMemo(() => ({ isDarkMode, toggleDarkMode }), [isDarkMode, toggleDarkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};